import {Component, Host, h, Prop, Method, type JSX} from '@stencil/core';
import { GuidedApiQuickLink, GuidedApiSuggestion } from '../api';
import axios from 'axios';
import {chipsBeforeForCategoryCreator, highlight, parseJsonWithFallback} from './utils';
import {authenticationHeaders} from '../subcomponents/utils';
import { makeLogger } from '../../../logger/logger';

const { err, info } = makeLogger({ pathToCurrentFile: 'components/guided/backends/cose-links.tsx', loggersList: ['err'] });

/**
 * @Deprecated use cosev2 instead
 */
@Component({
  tag: 'uiaas-guided-api-cose-quicklinks',
  shadow: true
})
export class GuidedApiCoseQuicklinks {

    @Prop() readonly term: string;
    @Prop() readonly api: string;

    @Prop() readonly language: string;
    @Prop() readonly country: string;
    @Prop() readonly disabled: boolean = false;
    /**
     * ex se/guided
     */
    @Prop() readonly searcher: string;
    @Prop() readonly maxQuickLinks: number = 5;
    /**
     * Maps cose $.groups[].id to proper guided section as in Quick Link Sections prop of the guided component.
     * If no mapping is defined, value is used as-is
    * */
    @Prop() readonly sectionMapping = JSON.stringify({
      'documentation-and-support':'support'
    });

    /**
     * Should we highlight the term in the suggestion?
     */
    @Prop() readonly allowHighlighting: boolean = false;
    @Prop() readonly allowFrontHighlighting: boolean = false;

    @Prop() readonly apikey?: string;
    @Prop() readonly bearer?: string;
    @Prop() readonly tokenUrl?: string;
    @Prop() readonly idmsToken?: string;
    @Prop() readonly apigeeBasicAuth?: string;
    @Prop() readonly groupOptions?: string;
    @Prop() readonly businessUnits?: string;

    /**
     * Custom Headres object Record<string,string> as json string
     */
    @Prop() readonly customHeaders: string = '{}';


    @Method() async fetch(term: string): Promise<Array<GuidedApiSuggestion>> {
        try{
          const url = this.api;

          const authHeaders = await authenticationHeaders({
            apikey: this.apikey,
            bearer: this.bearer,
            tokenUrl: this.tokenUrl,
            idmsToken: this.idmsToken,
            apigeeBasicAuth: this.apigeeBasicAuth,
          });

          const customHeaders = parseJsonWithFallback(this.customHeaders,{},'Failed to parse customHeaders, using default fallback');

          const headers = {...customHeaders, ...authHeaders};

          const requestBody = {
            'searcher': this.searcher,
            'country': this.country,
            'language': this.language,
            'term': term,
            'limitPerGroup': this.maxQuickLinks
          };

          if(this.searcher == 'cpq/quotes/guided'){
            const fixedBu = this.businessUnits.replace(/'/g , '"'); //Business units don't contain '
            const bu = JSON.parse(fixedBu);
            delete requestBody['country'];
            delete requestBody['language'];
            requestBody['context'] = {
              'businessUnits': bu,
              'language': this.language,
              'type': 'cose/context/businessUnits'
            };
          }

          if(this.groupOptions) {
            try {
              requestBody['groupOptions'] = JSON.parse(this.groupOptions);
            } catch (e) {
              info('Failed to parse groupOptions as JSON',this.groupOptions);
            }
          }

          const response = await axios.post(url, requestBody,{
            headers:{
              'content-type':'application/json',
              ...headers
            }
          });


          if (response.status !== 200) {
            err('Guided Universal backend response error:', response);
            return [];
          }
          const json = response.data;

          const r: Array<GuidedApiQuickLink> = [];

          if(json.groups.length == 0)
            return r;

          json.groups.forEach(group=>{

              const {id, displayName, documents} = group;

              documents.forEach(item=>{

                  let markup = this.allowHighlighting ? item.titleHtml : item.titlePlain;
                  markup = markup ?? '';

                  if (!markup) {
                    markup = item.quotationNumber || item.quoteNumber || item.referenceNumber || markup;
                  }
                  markup = markup.replace('<span>','<span class="highlight">');
                  const finalMarkup = this.allowFrontHighlighting ? highlight(term, markup) : markup;

                  /*
                    Create chips for category products by <id>
                   */
                  const chips = chipsBeforeForCategoryCreator(item, 'Products', [{ textKey: 'id'} ]);

                  const ready = GuidedApiQuickLink.from({
                    sectionId: this.mapSection(id),
                    markup: finalMarkup,
                    title: item.titlePlain,
                    openUrl: item.visitUrl,
                    image: item.imageUrl,
                    chips,
                    _original: {...item, sectionName: displayName},
                  });
                  r.push(ready);
              });
          });


          return r;

        }catch(e){
          err('cose guided api error',e);
          return [];
        }
    }
    render(): JSX.Element {
        return (
          <Host/>
        );
    }

    /**
     * Maps section id that comes from cose to proper guided section as in Quick Link Sections prop of the guided component.
     * if no mapping is defined, value is used as-is
     * @param id
     * @private
     */
    private mapSection(id: string) {
        const map = JSON.parse(this.sectionMapping);
        return map[id] || id;
    }
}
